// src/lib/school/assignment-length.ts
//
// How long an assigned test takes, and what to do about the bell.
//
// A teacher setting a test from the Assign form had no idea of its length, and
// neither did the student opening it: a full SAT is well over two hours, a
// class period is 45–90 minutes, and a module left running submits itself. So
// the Assign form, the student's assigned card and the teacher's panel all say
// how long it takes and that Pause is how to stop at the bell.
//
// Computed from the run config (src/lib/test-engine/test-run-config.ts), so
// the numbers can't drift from what the test actually does. Before any
// extended-time multiplier: a student with extended time takes longer.
//
// Pure: the teacher's panel is a client component.

import { TEST_RUN_CONFIGS } from '@/lib/test-engine/test-run-config'

const BREAK_MIN = 10

/** Minutes a full-length sitting of this kind takes, break included. */
export function testLengthMinutes(kind: 'CHECKPOINT' | 'FULL_TEST'): number {
  const cfg = TEST_RUN_CONFIGS[kind === 'CHECKPOINT' ? 'CHECKPOINT' : 'SAT']
  // Two modules per section.
  const testing = (2 * (cfg.seconds.RW + cfg.seconds.MATH)) / 60
  return testing + (cfg.hasBreak ? BREAK_MIN : 0)
}

export function formatMinutes(total: number): string {
  const h = Math.floor(total / 60)
  const m = total % 60
  if (h === 0) return `${m} minutes`
  const hours = h === 1 ? '1 hour' : `${h} hours`
  return m === 0 ? hours : `${hours} ${m} minutes`
}

/**
 * One line on length and the bell, for a test assignment; null for practice
 * (it has no clock). `audience` picks whose words.
 */
export function assignmentLengthNote(kind: string, audience: 'teacher' | 'student'): string | null {
  if (kind !== 'CHECKPOINT' && kind !== 'FULL_TEST') return null
  const length = formatMinutes(testLengthMinutes(kind))
  const withBreak = kind === 'FULL_TEST' ? ', including the break' : ''
  return audience === 'teacher'
    ? `About ${length}${withBreak}, longer with extended time. Students who stop at the bell should press Pause, which freezes their clock until they come back.`
    : `About ${length}${withBreak}. If you need to stop, like at the bell, press Pause: your clock stops until you come back.`
}
